import { useEffect, useId, useRef } from 'react'
import type { ReminderPreviewPayload } from '../types/chat'
import { useInlineFieldSpeechRecognition } from '../hooks/useInlineFieldSpeechRecognition'

export type ReminderPreviewEditField = 'purpose' | 'day' | 'time'

export type ReminderInlineVoiceTranscribeConfig = {
  websocketUrl: string
  apiBaseUrl?: string
  transcribeMode?: 1 | 2
  getMedicalLinkCredentials?: () => {
    piBearerToken: string
    loggedUserId: string
    medCenterId: string
    senderFbUid?: string
  }
  clientId?: string
  tenantId?: string
  sessionId?: string
  blocked?: boolean
  onBusyChange?: (busy: boolean) => void
}

function reminderPreviewFr(preview: ReminderPreviewPayload, uiLangFr: () => boolean): boolean {
  const langTag = (preview.language || '').toLowerCase()
  return langTag === 'french' || langTag === 'fr' || uiLangFr()
}

function PurposeInlineEditRow(props: {
  fr: boolean
  draft: string
  saving: boolean
  error: string | null
  languageHint?: string
  voiceTranscribe?: ReminderInlineVoiceTranscribeConfig
  onDraftChange: (value: string) => void
  onSave: () => void
  onCancel: () => void
}) {
  const { fr, draft, saving, error, languageHint, voiceTranscribe, onDraftChange, onSave, onCancel } =
    props
  const inputId = useId()
  const inputRef = useRef<HTMLInputElement>(null)
  const { supported, status, statusHint, error: speechError, toggle } = useInlineFieldSpeechRecognition({
    websocketUrl: voiceTranscribe?.websocketUrl ?? '',
    apiBaseUrl: voiceTranscribe?.apiBaseUrl,
    transcribeMode: voiceTranscribe?.transcribeMode,
    getMedicalLinkCredentials: voiceTranscribe?.getMedicalLinkCredentials,
    clientId: voiceTranscribe?.clientId,
    tenantId: voiceTranscribe?.tenantId,
    sessionId: voiceTranscribe?.sessionId,
    languageHint,
    disabled: saving,
    blocked: voiceTranscribe?.blocked,
    onBusyChange: voiceTranscribe?.onBusyChange,
    onTranscript: (text) => onDraftChange(text),
  })

  useEffect(() => {
    inputRef.current?.focus()
    inputRef.current?.select()
  }, [])

  const helper =
    speechError ||
    (status === 'listening' && statusHint.trim()
      ? statusHint.trim()
      : fr
        ? 'Appuyez sur le micro pour parler ou saisissez votre texte.'
        : 'Tap mic to speak or type to edit.')
  const micLabel =
    status === 'listening'
      ? fr
        ? 'Écoute en cours'
        : 'Listening'
      : fr
        ? 'Dicter l’objet'
        : 'Dictate purpose'

  return (
    <div className="vnw-sportsNocKvRow vnw-reminderInlineEditRow" role="listitem">
      <div className="vnw-sportsNocKvKey">{fr ? 'Objet' : 'Purpose'}</div>
      <div className="vnw-reminderInlineEditBody">
        <label className="vnw-reminderInlineEditLabel" htmlFor={inputId}>
          {fr ? 'Modifier l’objet' : 'Edit purpose'}
        </label>
        <div className="vnw-reminderInlineEditInputWrap">
          <input
            id={inputId}
            ref={inputRef}
            type="text"
            className="vnw-reminderInlineEditInput"
            value={draft}
            disabled={saving}
            autoComplete="off"
            enterKeyHint="done"
            onChange={(e) => onDraftChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                onSave()
              }
              if (e.key === 'Escape') {
                e.preventDefault()
                onCancel()
              }
            }}
          />
          <button
            type="button"
            className={
              'vnw-reminderInlineEditMic' + (status === 'listening' ? ' vnw-reminderInlineEditMic--active' : '')
            }
            aria-label={micLabel}
            title={supported ? micLabel : fr ? 'Saisie vocale indisponible' : 'Voice input unavailable'}
            disabled={saving || !supported}
            aria-pressed={status === 'listening'}
            onClick={() => toggle()}
          >
            <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" focusable="false">
              <path
                fill="currentColor"
                d="M12 14a3 3 0 0 0 3-3V5a3 3 0 1 0-6 0v6a3 3 0 0 0 3 3Zm5-3a5 5 0 0 1-10 0H5a7 7 0 0 0 6 6.93V21h2v-3.07A7 7 0 0 0 19 11h-2Z"
              />
            </svg>
          </button>
        </div>
        <p className="vnw-reminderInlineEditHelper" role="status">
          {error || helper}
        </p>
        <div className="vnw-reminderInlineEditActions">
          <button
            type="button"
            className="vnw-reminderInlineEditBtn vnw-reminderInlineEditBtn--primary"
            disabled={saving || !draft.trim()}
            onClick={onSave}
          >
            {fr ? 'Enregistrer' : 'Save'}
          </button>
          <button
            type="button"
            className="vnw-reminderInlineEditBtn vnw-reminderInlineEditBtn--ghost"
            disabled={saving}
            onClick={onCancel}
          >
            {fr ? 'Annuler' : 'Cancel'}
          </button>
        </div>
      </div>
    </div>
  )
}

function StructuredInlineEditRow(props: {
  fr: boolean
  field: 'day' | 'time'
  draft: string
  saving: boolean
  error: string | null
  onDraftChange: (value: string) => void
  onSave: () => void
  onCancel: () => void
}) {
  const { fr, field, draft, saving, error, onDraftChange, onSave, onCancel } = props
  const inputId = useId()
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    inputRef.current?.focus()
  }, [])

  const label = field === 'day' ? (fr ? 'Jour' : 'Day') : fr ? 'Heure' : 'Time'
  const editTitle = field === 'day' ? (fr ? 'Modifier le jour' : 'Edit day') : fr ? "Modifier l'heure" : 'Edit time'

  return (
    <div className="vnw-sportsNocKvRow vnw-reminderInlineEditRow" role="listitem">
      <div className="vnw-sportsNocKvKey">{label}</div>
      <div className="vnw-reminderInlineEditBody">
        <label className="vnw-reminderInlineEditLabel" htmlFor={inputId}>
          {editTitle}
        </label>
        <input
          id={inputId}
          ref={inputRef}
          type={field === 'day' ? 'date' : 'time'}
          className="vnw-reminderInlineEditInput vnw-reminderInlineEditInput--structured"
          value={draft}
          disabled={saving}
          onChange={(e) => onDraftChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              onSave()
            }
            if (e.key === 'Escape') {
              e.preventDefault()
              onCancel()
            }
          }}
        />
        {error ? (
          <p className="vnw-reminderInlineEditHelper vnw-reminderInlineEditHelper--error" role="alert">
            {error}
          </p>
        ) : null}
        <div className="vnw-reminderInlineEditActions">
          <button
            type="button"
            className="vnw-reminderInlineEditBtn vnw-reminderInlineEditBtn--primary"
            disabled={saving || !draft.trim()}
            onClick={onSave}
          >
            {fr ? 'Enregistrer' : 'Save'}
          </button>
          <button
            type="button"
            className="vnw-reminderInlineEditBtn vnw-reminderInlineEditBtn--ghost"
            disabled={saving}
            onClick={onCancel}
          >
            {fr ? 'Annuler' : 'Cancel'}
          </button>
        </div>
      </div>
    </div>
  )
}

export type ReminderPreviewInlineEditProps = {
  inlineEdit?: ReminderPreviewEditField | null
  inlineDraft?: string
  inlineSaving?: boolean
  inlineError?: string | null
  voiceTranscribe?: ReminderInlineVoiceTranscribeConfig
  onInlineDraftChange?: (value: string) => void
  onInlineSave?: () => void
  onInlineCancel?: () => void
}

export function ReminderPreviewInlineRows(props: {
  preview: ReminderPreviewPayload
  fr: boolean
  editLabel: string
  editing: boolean
  actions?: {
    onEditPurpose: () => void
    onEditDay: () => void
    onEditTime: () => void
  }
  inline: ReminderPreviewInlineEditProps
  /** Hide per-row Edit when the reminder is finalized or actions are disabled. */
  showEditActions?: boolean
}) {
  const { preview, fr, editLabel, editing, actions, inline, showEditActions = true } = props
  const editVisible = Boolean(actions && showEditActions && !editing)
  const {
    inlineEdit,
    inlineDraft = '',
    inlineSaving = false,
    inlineError,
    voiceTranscribe,
    onInlineDraftChange,
    onInlineSave,
    onInlineCancel,
  } = inline

  return (
    <>
      {inlineEdit === 'purpose' && onInlineDraftChange && onInlineSave && onInlineCancel ? (
        <PurposeInlineEditRow
          fr={fr}
          draft={inlineDraft}
          saving={inlineSaving}
          error={inlineError ?? null}
          languageHint={preview.language}
          voiceTranscribe={voiceTranscribe}
          onDraftChange={onInlineDraftChange}
          onSave={onInlineSave}
          onCancel={onInlineCancel}
        />
      ) : (
        <div className="vnw-sportsNocKvRow" role="listitem">
          <div className="vnw-sportsNocKvKey">{fr ? 'Objet' : 'Purpose'}</div>
          <div className="vnw-sportsNocKvVal">{preview.purpose?.trim() || '—'}</div>
          <div className="vnw-sportsNocKvAction">
            {editVisible ? (
              <button type="button" className="vnw-sportsNocKvEdit" onClick={actions!.onEditPurpose}>
                {editLabel}
              </button>
            ) : null}
          </div>
        </div>
      )}

      {inlineEdit === 'day' && onInlineDraftChange && onInlineSave && onInlineCancel ? (
        <StructuredInlineEditRow
          fr={fr}
          field="day"
          draft={inlineDraft}
          saving={inlineSaving}
          error={inlineError ?? null}
          onDraftChange={onInlineDraftChange}
          onSave={onInlineSave}
          onCancel={onInlineCancel}
        />
      ) : (
        <div className="vnw-sportsNocKvRow" role="listitem">
          <div className="vnw-sportsNocKvKey">{fr ? 'Jour' : 'Day'}</div>
          <div className="vnw-sportsNocKvVal">{preview.day?.trim() || '—'}</div>
          <div className="vnw-sportsNocKvAction">
            {editVisible ? (
              <button type="button" className="vnw-sportsNocKvEdit" onClick={actions!.onEditDay}>
                {editLabel}
              </button>
            ) : null}
          </div>
        </div>
      )}

      {inlineEdit === 'time' && onInlineDraftChange && onInlineSave && onInlineCancel ? (
        <StructuredInlineEditRow
          fr={fr}
          field="time"
          draft={inlineDraft}
          saving={inlineSaving}
          error={inlineError ?? null}
          onDraftChange={onInlineDraftChange}
          onSave={onInlineSave}
          onCancel={onInlineCancel}
        />
      ) : (
        <div className="vnw-sportsNocKvRow" role="listitem">
          <div className="vnw-sportsNocKvKey">{fr ? 'Heure' : 'Time'}</div>
          <div className="vnw-sportsNocKvVal">{preview.time?.trim() || '—'}</div>
          <div className="vnw-sportsNocKvAction">
            {editVisible ? (
              <button type="button" className="vnw-sportsNocKvEdit" onClick={actions!.onEditTime}>
                {editLabel}
              </button>
            ) : null}
          </div>
        </div>
      )}
    </>
  )
}

export function reminderPreviewCardFr(preview: ReminderPreviewPayload, uiLangFr: () => boolean): boolean {
  return reminderPreviewFr(preview, uiLangFr)
}
